import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  ScanSearch,
  FishSymbol,
  Bell,
  Cpu,
  Smartphone,
  Globe,
  ArrowRight,
  Lock,
  Check,
  Crown,
  Rocket,
  X,
  Download,
} from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5JTIwZGF0YSUyMGFic3RyYWN0fGVufDB8fHxibGFja3wxNzg0MDkyNjE0fDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: FishSymbol, title: "AI Phishing Checker", desc: "Paste any URL, email or message. Our AI engine grades the risk and explains every red flag it finds." },
  { icon: Globe, title: "Malicious Link Intel", desc: "Every link is checked against VirusTotal's 70+ security engines so you know what's safe before you click." },
  { icon: ScanSearch, title: "Security Scans", desc: "Run on-demand and scheduled security scans, review flagged risks, and export a shareable PDF report." },
  { icon: Bell, title: "Real-time Alerts", desc: "A live activity log of every check, scan and event across your protected surface." },
  { icon: Smartphone, title: "Works Everywhere", desc: "Check suspicious SMS, links and messages from your phone or desktop — one console, any device." },
  { icon: Cpu, title: "Security Command Center", desc: "Track your security posture and manage every protection module from a single dashboard." },
];

export default function Landing() {
  const [plans, setPlans] = useState(null);
  const [banner, setBanner] = useState(true);
  useEffect(() => {
    api.get("/pricing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Launch banner */}
      {banner && (
        <div data-testid="launch-banner" className="fixed top-0 inset-x-0 z-[60] bg-primary text-primary-foreground">
          <div className="max-w-7xl mx-auto px-6 h-10 flex items-center justify-center relative">
            <div className="flex items-center gap-2 font-mono text-[11px] sm:text-sm font-bold tracking-tight text-center">
              <Rocket size={15} strokeWidth={2} className="shrink-0" />
              <span>Just launched — Plyx Cyber Guard is live. Protect yourself from phishing today.</span>
            </div>
            <button
              data-testid="dismiss-banner"
              onClick={() => setBanner(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
              aria-label="Dismiss"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {/* Nav */}
      <header className={`fixed ${banner ? "top-10" : "top-0"} inset-x-0 z-50 bg-background/80 backdrop-blur-md border-b border-border transition-[top] duration-200`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <a href="#pricing" className="hidden sm:inline font-mono text-sm text-muted-foreground hover:text-primary transition-colors mr-2">
              Pricing
            </a>
            <Link to="/install" className="hidden sm:inline font-mono text-sm text-muted-foreground hover:text-primary transition-colors mr-2">
              Get the App
            </Link>
            <Link to="/login">
              <Button data-testid="nav-login-btn" variant="ghost" className="rounded-none font-mono text-sm text-muted-foreground hover:text-primary">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-register-btn" className="rounded-none font-mono text-sm bg-primary text-primary-foreground hover:bg-primary/90 glow-green">
                Get protected
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-28 overflow-hidden grid-bg">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary border border-primary/40 px-4 py-1.5 mb-8 animate-fade-up">
            <span className="w-1.5 h-1.5 bg-primary animate-pulse-glow" />
            System secured // v2.6
          </div>
          <h1 className="font-mono font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] animate-fade-up" style={{ animationDelay: "80ms" }}>
            Stop phishing, scams &amp;
            <br />
            <span className="text-primary text-glow">malicious links cold</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: "160ms" }}>
            Plyx Cyber Guard is an AI-powered security console that analyzes suspicious URLs, emails and messages in real time — backed by VirusTotal's 70+ threat engines — and keeps a live log of every risk it finds.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/register">
              <Button data-testid="hero-cta-btn" size="lg" className="rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green group">
                Launch security console
                <ArrowRight size={18} strokeWidth={1.5} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="hero-secondary-btn" size="lg" variant="outline" className="rounded-none font-mono border-border hover:border-primary hover:text-primary">
                <Lock size={16} strokeWidth={1.5} className="mr-2" /> I have an account
              </Button>
            </Link>
            <Link to="/install">
              <Button data-testid="hero-install-btn" size="lg" variant="outline" className="rounded-none font-mono border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
                <Download size={16} strokeWidth={1.5} className="mr-2" /> Get the App
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[
            ["70+", "Threat engines"],
            ["Real-time", "AI analysis"],
            ["VirusTotal", "reputation intel"],
            ["24/7", "Threat monitoring"],
          ].map(([n, l], i) => (
            <div key={i} className="py-8 px-4 text-center">
              <div className="font-mono font-bold text-2xl md:text-3xl text-primary text-glow">{n}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-14">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Capabilities</div>
          <h2 className="font-mono font-bold tracking-tighter text-3xl md:text-4xl max-w-2xl">
            A complete defense grid in one console
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className="group bg-card p-8 hover:bg-primary/[0.03] transition-colors duration-200"
              >
                <div className="w-11 h-11 flex items-center justify-center border border-primary/40 bg-primary/10 mb-5 group-hover:glow-green transition-shadow duration-200">
                  <Icon size={22} strokeWidth={1.5} className="text-primary" />
                </div>
                <h3 className="font-mono font-bold text-lg tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="mb-14 text-center">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Pricing</div>
            <h2 className="font-mono font-bold tracking-tighter text-3xl md:text-4xl">
              Protection for every threat level
            </h2>
          </div>
          {plans && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
              {["free", "pro", "enterprise"].map((id) => {
                const p = plans[id];
                const featured = id === "pro";
                return (
                  <div key={id} className={`relative bg-card p-8 flex flex-col ${featured ? "ring-1 ring-primary" : ""}`}>
                    {featured && (
                      <div className="absolute top-0 right-0 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest px-3 py-1">
                        Popular
                      </div>
                    )}
                    <Crown size={22} strokeWidth={1.5} className={featured ? "text-primary" : "text-muted-foreground"} />
                    <h3 className="font-mono font-bold text-xl tracking-tight mt-4">{p.name}</h3>
                    <div className="mt-3 flex items-end gap-1">
                      <span className="font-mono font-bold text-4xl">${p.amount}</span>
                      <span className="font-mono text-sm text-muted-foreground mb-1">/{p.period}</span>
                    </div>
                    <ul className="mt-6 space-y-3 flex-1">
                      {p.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm">
                          <Check size={16} strokeWidth={2} className="text-primary mt-0.5 shrink-0" />
                          <span className="text-foreground/90">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className="mt-8">
                      <Button
                        data-testid={`landing-plan-${id}`}
                        className={`w-full rounded-none font-mono ${
                          featured
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
                            : "bg-secondary text-foreground hover:bg-accent border border-border"
                        }`}
                      >
                        {id === "free" ? "Start free" : `Get ${p.name}`}
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <ShieldCheck size={40} strokeWidth={1.5} className="text-primary text-glow mx-auto mb-6" />
          <h2 className="font-mono font-bold tracking-tighter text-3xl md:text-4xl">
            Your perimeter is one click away
          </h2>
          <p className="mt-4 text-muted-foreground">Create your free account and run your first threat scan in seconds.</p>
          <Link to="/register">
            <Button data-testid="footer-cta-btn" size="lg" className="mt-8 rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green">
              Get protected now
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="font-mono text-xs text-muted-foreground">© 2026 Plyx Cyber Guard. All systems operational.</p>
        </div>
      </footer>
    </div>
  );
}
